/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/


// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";


import { useMemo, useRef, useState } from "react";
import moment from "moment";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from '@mui/material/Box';
import Divider from "@mui/material/Divider";
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
const humanizeDuration = require("humanize-duration");

import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";

import PlaceholderCard from "examples/Cards/PlaceholderCard";


// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftDatePicker from "components/SoftDatePicker";

// Soft UI Dashboard PRO React example components

import Calendar from "nextus/Calendars/Calendar";
import breakpoints from "assets/theme/base/breakpoints";


const mapbox_settings = {
    style: 'mapbox://styles/harrymontano/cm01omrnz000u01omf2uegk93?optimize=true',
    zoom: 14,
    // maxZoom: 5
}

function getSecondsBetweenDates(start_time, end_time) {
    const start_time_moment = moment(start_time);
    const end_time_moment = moment(end_time);
    if (start_time_moment.isValid() && end_time_moment.isValid()) {
        return end_time_moment.diff(start_time_moment, 'seconds');
    }
}

function generateMomentString(input){
    // console.log(input)
    let format = "h:mm A - ddd Do MMM";
    if(!input.isSame(new Date(), 'year')){
        format += " YYYY"
    }

    return input.format(format);
}

var variant_type = () => {
    if(window.innerWidth > breakpoints.values.x3l || (window.innerWidth > breakpoints.values.md && window.innerWidth <= breakpoints.values.lg)){
        return 'body1';
    }
    else if(window.innerWidth > breakpoints.values.sm){
        return 'body2';
    }

    return "mobileBody1";
}

function ClearwayTimingPreview({ timingData, variant = 'body1' }) {

    const seconds_in_advance = getSecondsBetweenDates(timingData.display_time, timingData.start_time);
    const seconds_on_display = getSecondsBetweenDates(timingData.start_time, timingData.end_time);

    const display_time = moment(timingData.display_time);
    const start_time = moment(timingData.start_time);
    const end_time = moment(timingData.end_time);

    return (<>
        <Timeline>
            <TimelineItem>
                <TimelineOppositeContent variant={variant_type()} color="text.secondary" sx={{ m: 'auto 0' }}>{generateMomentString(display_time)}</TimelineOppositeContent>
                <TimelineSeparator sx={{ m: 'auto 0' }}>
                    <TimelineDot />
                    <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ m: 'auto 0' }} variant={variant_type()}>Event Warning Starts</TimelineContent>
            </TimelineItem>
            <TimelineItem>
                <TimelineOppositeContent variant={variant_type()} color="text.secondary" sx={{ m: 'auto 0' }}>{humanizeDuration(seconds_in_advance * 1000)}</TimelineOppositeContent>
                <TimelineSeparator>
                    <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ m: 'auto 0' }} variant={variant_type()}>Warning Period</TimelineContent>
            </TimelineItem>
            <TimelineItem>
                <TimelineOppositeContent variant={variant_type()} color="text.secondary" sx={{ m: 'auto 0' }}>{generateMomentString(start_time)}</TimelineOppositeContent>
                <TimelineSeparator sx={{ m: 'auto 0' }}>
                    <TimelineDot />
                    <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ m: 'auto 0' }} variant={variant_type()}>Special Event Starts</TimelineContent>
            </TimelineItem>
            <TimelineItem>
                <TimelineOppositeContent variant={variant_type()} color="text.secondary" sx={{ m: 'auto 0' }}>{humanizeDuration(seconds_on_display * 1000)}</TimelineOppositeContent>
                <TimelineSeparator>
                    <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ m: 'auto 0' }} variant={variant_type()}>Display Period</TimelineContent>
            </TimelineItem>
            <TimelineItem>
                <TimelineOppositeContent variant={variant_type()} color="text.secondary" sx={{ m: 'auto 0' }}>{generateMomentString(end_time)}</TimelineOppositeContent>
                <TimelineSeparator sx={{ m: 'auto 0' }}>
                    <TimelineDot />
                </TimelineSeparator>
                <TimelineContent sx={{ m: 'auto 0' }} variant={variant_type()}>Special Event Ends</TimelineContent>
            </TimelineItem>
        </Timeline>
    </>);
}

// Typechecking props for the Calendar
ClearwayTimingPreview.propTypes = {
    timingData: PropTypes.object,
    variant: PropTypes.string
};


export default ClearwayTimingPreview;